import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useAnchoredOverlay } from "@/hooks/useAnchoredOverlay";
import { IconPlus, IconSettings, IconX } from "../../../components/icons";
import {
  BODY_TEMPLATE_SYSTEM_SOURCES,
  defaultBodyTemplate,
  defaultJsonForSource,
  insertFormFieldIntoTemplate,
  insertSystemSourceIntoTemplate,
  listTemplatePlaceholderRefs,
  parseFieldPropertySource,
  patchTemplatePlaceholder,
  placeholderText,
  validateBodyTemplateJson,
  type TemplatePlaceholderRef,
} from "../../../lib/formBuilder/bodyTemplateEditor";
import { isListFieldType } from "../../../lib/formBuilder/listChoicesMode";
import { Checkbox, FieldLabel, SelectInput } from "../_components/AdminShell";

export type FormFieldOption = {
  name: string;
  label: string;
  type: string;
};

function webhookSourceOptions(field: FormFieldOption): Array<{
  value: string;
  label: string;
}> {
  const title = field.label.trim() || field.name;
  const options = [{ value: field.name, label: title }];
  if (isListFieldType(field.type)) {
    options.push(
      { value: `${field.name}.label`, label: `${title} · label` },
      { value: `${field.name}.value`, label: `${title} · value` },
    );
  }
  return options;
}

function isSystemSource(source: string): boolean {
  return BODY_TEMPLATE_SYSTEM_SOURCES.some((item) => item.id === source);
}

function isKnownSource(
  source: string,
  formFields: FormFieldOption[],
  fioVars: Array<{ id: string }>,
): boolean {
  if (!source.trim()) return false;
  if (isSystemSource(source)) return true;
  if (fioVars.some((item) => item.id === source)) return true;
  if (formFields.some((field) => field.name === source)) return true;
  const labeled = parseFieldPropertySource(source);
  return Boolean(
    labeled && formFields.some((field) => field.name === labeled.field),
  );
}

function sourceFieldError(
  source: string,
  formFields: FormFieldOption[],
  fioVars: Array<{ id: string }>,
): string | null {
  if (!source.trim()) return "Выберите источник";
  if (isKnownSource(source, formFields, fioVars)) return null;
  return `Поле «${source}» не найдено в форме`;
}

function sourceChipLabel(
  source: string,
  formFields: FormFieldOption[],
  fioVars: Array<{ id: string; label: string }>,
): string {
  const field = formFields.find((item) => item.name === source);
  if (field) return field.label.trim() || field.name;

  const labeled = parseFieldPropertySource(source);
  if (labeled) {
    const named = formFields.find((item) => item.name === labeled.field);
    if (named) {
      return `${named.label.trim() || named.name} · ${labeled.property}`;
    }
  }

  const fio = fioVars.find((item) => item.id === source);
  if (fio) {
    const match = fio.label.match(/\((.+)\)$/);
    return match ? match[1] : fio.label;
  }

  const system = BODY_TEMPLATE_SYSTEM_SOURCES.find(
    (item) => item.id === source,
  );
  if (system) return system.label;

  return source;
}

function MacrosChipButton({
  title,
  onClick,
  children,
  className = "",
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={[
        "inline-flex shrink-0 items-center justify-center text-[#8a919a]",
        "transition-colors hover:text-[#5c6370]",
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function ChipSettingsPanel({
  placeholderRef,
  formFields,
  fioVars,
  overlayRef,
  onUpdate,
  onClose,
}: {
  placeholderRef: TemplatePlaceholderRef;
  formFields: FormFieldOption[];
  fioVars: Array<{ id: string; label: string }>;
  overlayRef: RefObject<HTMLDivElement | null>;
  onUpdate: (patch: { source?: string; useJsonFilter?: boolean }) => void;
  onClose: () => void;
}) {
  return (
    <div
      ref={overlayRef}
      data-admin-overlay=""
      className="fixed z-[60] w-56 rounded-lg border border-slate-200 bg-white p-2.5 shadow-lg"
      style={{ visibility: "hidden" }}
    >
      <p className="mb-1.5 font-onest text-[11px] font-medium text-slate-500">
        Источник значения
      </p>
      <SelectInput
        size="compact"
        value={placeholderRef.source}
        onChange={(source) => {
          const patch: { source: string; useJsonFilter?: boolean } = { source };
          if (source) patch.useJsonFilter = defaultJsonForSource(source);
          onUpdate(patch);
        }}
      >
        <option value="">— выберите —</option>
        {formFields.length > 0 ? (
          <optgroup label="Поля формы">
            {formFields.flatMap((field) =>
              webhookSourceOptions(field).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              )),
            )}
          </optgroup>
        ) : null}
        {fioVars.length > 0 ? (
          <optgroup label="Из ФИО">
            {fioVars.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </optgroup>
        ) : null}
        <optgroup label="Системные">
          {BODY_TEMPLATE_SYSTEM_SOURCES.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </optgroup>
        {placeholderRef.source &&
        !isKnownSource(placeholderRef.source, formFields, fioVars) ? (
          <option value={placeholderRef.source}>{placeholderRef.source}</option>
        ) : null}
      </SelectInput>
      <div className="mt-2">
        <Checkbox
          id={`chip-json-${placeholderRef.id}`}
          size="compact"
          checked={placeholderRef.useJsonFilter}
          onChange={(useJsonFilter) => onUpdate({ useJsonFilter })}
          label="Фильтр json"
        />
      </div>
      <p className="mt-2 font-mono text-[10px] text-slate-400">
        {placeholderText(placeholderRef.source, placeholderRef.useJsonFilter)}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-2 font-onest text-[11px] font-medium text-brand-600 hover:text-brand-500"
      >
        Готово
      </button>
    </div>
  );
}

function PlaceholderChip({
  placeholderRef,
  formFields,
  fioVars,
  onUpdate,
  onRemove,
}: {
  placeholderRef: TemplatePlaceholderRef;
  formFields: FormFieldOption[];
  fioVars: Array<{ id: string; label: string }>;
  onUpdate: (patch: { source?: string; useJsonFilter?: boolean }) => void;
  onRemove: () => void;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const sourceError = sourceFieldError(
    placeholderRef.source,
    formFields,
    fioVars,
  );
  const label = sourceChipLabel(placeholderRef.source, formFields, fioVars);
  const system = isSystemSource(placeholderRef.source);
  useAnchoredOverlay({
    open: settingsOpen && !system,
    triggerRef,
    overlayRef,
    align: "start",
    matchTriggerWidth: false,
    minHeight: 160,
  });

  useEffect(() => {
    if (!settingsOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (overlayRef.current?.contains(target)) return;
      setSettingsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [settingsOpen]);

  return (
    <span ref={rootRef} className="relative inline-flex">
      <span
        className={[
          "inline-flex max-w-[16rem] items-center gap-0.5 rounded px-1 py-px",
          "bg-[#eef1f6] text-[#2d3748]",
          sourceError ? "outline outline-1 outline-red-300" : "",
        ].join(" ")}
        title={
          sourceError ??
          placeholderText(placeholderRef.source, placeholderRef.useJsonFilter)
        }
      >
        {!system ? (
          <span ref={triggerRef} className="inline-flex">
            <MacrosChipButton
              title="Настроить"
              onClick={() => setSettingsOpen((open) => !open)}
              className="h-[18px] w-[18px] rounded hover:bg-black/[0.06]"
            >
              <IconSettings size={14} />
            </MacrosChipButton>
          </span>
        ) : null}
        <MacrosChipButton
          title="Удалить"
          onClick={onRemove}
          className="h-[18px] w-[18px] rounded hover:bg-black/[0.06]"
        >
          <IconX size={8} />
        </MacrosChipButton>
        <span className="min-w-0 truncate px-0.5 font-onest text-[13px] leading-snug">
          {label}
        </span>
      </span>
      {settingsOpen && !system
        ? createPortal(
            <ChipSettingsPanel
              placeholderRef={placeholderRef}
              formFields={formFields}
              fioVars={fioVars}
              overlayRef={overlayRef}
              onUpdate={(patch) => {
                onUpdate(patch);
                setSettingsOpen(false);
              }}
              onClose={() => setSettingsOpen(false)}
            />,
            document.body,
          )
        : null}
    </span>
  );
}

function AddFieldMenu({
  formFields,
  mappedSources,
  onAddField,
  onAddSystem,
}: {
  formFields: FormFieldOption[];
  mappedSources: Set<string>;
  onAddField: (field: FormFieldOption) => void;
  onAddSystem: (sourceId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  useAnchoredOverlay({
    open,
    triggerRef,
    overlayRef,
    align: "start",
    matchTriggerWidth: false,
    minHeight: 160,
  });

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (overlayRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        title="Добавить поле"
        onClick={() => setOpen((value) => !value)}
        className={[
          "inline-flex h-8 w-8 items-center justify-center rounded-full",
          "bg-[#f0f2f5] text-[#6b7280] transition-colors",
          "hover:bg-[#e4e7ec] hover:text-[#4b5563]",
          open ? "bg-[#e4e7ec] text-[#374151]" : "",
        ].join(" ")}
      >
        <IconPlus size={16} />
      </button>
      {open
        ? createPortal(
            <div
              ref={overlayRef}
              data-admin-overlay=""
              className="fixed z-[60] max-h-64 w-56 overflow-y-auto rounded-lg border border-[#d9dde4] bg-white py-1 shadow-[0_8px_28px_rgba(0,0,0,0.12)]"
              style={{ visibility: "hidden" }}
            >
              {formFields.length > 0 ? (
                <>
                  <p className="px-2.5 py-1 font-onest text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Поля формы
                  </p>
                  {formFields.map((field) => (
                    <button
                      key={field.name}
                      type="button"
                      onClick={() => {
                        onAddField(field);
                        setOpen(false);
                      }}
                      className={[
                        "flex w-full items-center gap-2 px-2.5 py-1.5 text-left font-onest text-xs transition-colors hover:bg-slate-50",
                        mappedSources.has(field.name)
                          ? "text-brand-700"
                          : "text-slate-700",
                      ].join(" ")}
                    >
                      {mappedSources.has(field.name) ? (
                        <span className="text-brand-500">✓</span>
                      ) : (
                        <span className="w-3" />
                      )}
                      {field.label.trim() || field.name}
                    </button>
                  ))}
                </>
              ) : null}
              <p className="mt-1 border-t border-slate-100 px-2.5 py-1 font-onest text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Системные
              </p>
              {BODY_TEMPLATE_SYSTEM_SOURCES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onAddSystem(item.id);
                    setOpen(false);
                  }}
                  className="flex w-full px-2.5 py-1.5 text-left font-onest text-xs text-slate-700 transition-colors hover:bg-slate-50"
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function fioTemplateVarOptions(
  fioSplit?: Record<string, unknown>,
): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  for (const config of Object.values(fioSplit ?? {})) {
    if (!config || typeof config !== "object") continue;
    const parts = (config as Record<string, unknown>).parts;
    if (!parts || typeof parts !== "object") continue;
    for (const [partKey, varName] of Object.entries(
      parts as Record<string, string>,
    )) {
      if (!varName) continue;
      const partLabels: Record<string, string> = {
        last_name: "Фамилия",
        first_name: "Имя",
        patronymic: "Отчество",
      };
      const partLabel = partLabels[partKey] ?? partKey;
      out.push({ id: varName, label: `${varName} (${partLabel})` });
    }
  }
  return out;
}

function ValidationStatus({
  validation,
}: {
  validation: ReturnType<typeof validateBodyTemplateJson>;
}) {
  if (validation.valid) {
    return (
      <span className="font-onest text-[11px] text-emerald-600">
        JSON корректен
      </span>
    );
  }

  return (
    <span
      className="max-w-[16rem] truncate font-onest text-[11px] text-red-600"
      title={
        validation.line
          ? `${validation.error} (строка ${validation.line}${validation.column ? `, символ ${validation.column}` : ""})`
          : validation.error
      }
    >
      {validation.error}
      {validation.line ? ` · строка ${validation.line}` : ""}
    </span>
  );
}

export function BodyTemplateEditor({
  resetKey: _resetKey,
  value,
  onChange,
  formFields,
  fioSplit,
}: {
  resetKey: string;
  value: string;
  onChange: (next: string) => void;
  formFields: FormFieldOption[];
  fioSplit?: Record<string, unknown>;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLPreElement>(null);
  const [editorFocused, setEditorFocused] = useState(false);

  const template = value || defaultBodyTemplate();
  const fioVars = useMemo(() => fioTemplateVarOptions(fioSplit), [fioSplit]);
  const validation = useMemo(
    () => validateBodyTemplateJson(template),
    [template],
  );
  const placeholders = useMemo(
    () => listTemplatePlaceholderRefs(template),
    [template],
  );
  const mappedSources = new Set(placeholders.map((item) => item.source));
  const lineCount = Math.max(template.split("\n").length, 12);

  const syncScroll = () => {
    if (textareaRef.current && gutterRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const patchPlaceholder = (
    ref: TemplatePlaceholderRef,
    patch: { source?: string; useJsonFilter?: boolean; remove?: boolean },
  ) => {
    onChange(patchTemplatePlaceholder(template, ref, patch));
  };

  const borderClass = editorFocused
    ? "border-[#5282ff] ring-2 ring-[#5282ff]/15"
    : validation.valid
      ? "border-[#d9dde4]"
      : "border-red-300 ring-2 ring-red-100";

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel size="compact">Тело запроса (bodyTemplate)</FieldLabel>
        <ValidationStatus validation={validation} />
      </div>

      <div className="relative mt-1.5">
        <div
          className="relative overflow-hidden rounded-lg bg-white"
          onFocusCapture={() => setEditorFocused(true)}
          onBlurCapture={(event) => {
            if (
              !event.currentTarget.contains(event.relatedTarget as Node | null)
            ) {
              setEditorFocused(false);
            }
          }}
        >
          <div
            className={[
              "pointer-events-none absolute inset-0 rounded-lg border transition-colors",
              borderClass,
            ].join(" ")}
            aria-hidden
          />

          <div className="flex max-h-[22rem] min-h-[11rem]">
            <pre
              ref={gutterRef}
              aria-hidden
              className="m-0 shrink-0 overflow-hidden border-r border-[#eef1f6] bg-[#fafbfc] py-3 pl-3 pr-2 text-right font-mono text-[11px] leading-[1.7] text-slate-300 select-none"
            >
              {Array.from({ length: lineCount }, (_, index) => (
                <div
                  key={index}
                  className={
                    !validation.valid && validation.line === index + 1
                      ? "text-red-400"
                      : undefined
                  }
                >
                  {index + 1}
                </div>
              ))}
            </pre>

            <textarea
              ref={textareaRef}
              value={template}
              spellCheck={false}
              onChange={(event) => onChange(event.target.value)}
              onScroll={syncScroll}
              className={[
                "min-h-[11rem] flex-1 resize-y border-0 bg-transparent py-3 pr-3 pl-2",
                "font-mono text-[13px] leading-[1.7] text-[#2d3748] focus:outline-none focus:ring-0",
              ].join(" ")}
              rows={lineCount}
            />
          </div>

          <div className="absolute bottom-2 left-2">
            <AddFieldMenu
              formFields={formFields}
              mappedSources={mappedSources}
              onAddField={(field) =>
                onChange(insertFormFieldIntoTemplate(template, field))
              }
              onAddSystem={(sourceId) =>
                onChange(insertSystemSourceIntoTemplate(template, sourceId))
              }
            />
          </div>
        </div>
      </div>

      {placeholders.length > 0 ? (
        <div className="mt-2">
          <p className="mb-1.5 font-onest text-[11px] text-slate-500">
            Переменные в шаблоне
          </p>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((placeholderRef) => (
              <PlaceholderChip
                key={`${placeholderRef.id}-${placeholderRef.start}`}
                placeholderRef={placeholderRef}
                formFields={formFields}
                fioVars={fioVars}
                onUpdate={(patch) => patchPlaceholder(placeholderRef, patch)}
                onRemove={() =>
                  patchPlaceholder(placeholderRef, { remove: true })
                }
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
